<script lang="ts">
import { defineComponent } from "vue";
import { mapGetters } from "vuex";

import { Statsig } from "@/env/statsig/client";
import { subscribeMailingList } from "@/services/profileService/mailingList";
import { sendButtonCtaTelemetry } from "@/utils/ctaTelemetry";
import isUnauth from "@/utils/isUnauth";
import leftShapeBadges from "@/views/ForYou/assets/leftShapeBadges.svg";

export default defineComponent({
  name: "NewsletterBanner",

  data() {
    return {
      consentChecked: false,
      gateEnabledEmailCommunication: false,
      isSubmitting: false,
      submitSuccess: false,
      submitError: false,
      validationError: false,
      leftShapeBadges,
    };
  },

  computed: {
    isUnauth,
    ...mapGetters({
      userStatus: "user/status",
    }),
    isSubscribed(): boolean {
      return this.userStatus?.mailing_list_subscribed;
    },
    showBanner(): boolean {
      return (
        this.gateEnabledEmailCommunication &&
        !this.isUnauth &&
        (!this.isSubscribed || this.submitSuccess)
      );
    },
  },

  async mounted() {
    if (this.isUnauth) return;
    this.gateEnabledEmailCommunication = await Statsig.featureGate(
      "EMAIL_NOTIFICATION_OPT_IN"
    );
  },

  methods: {
    async handleSubmit() {
      if (this.isSubmitting) {
        return;
      }

      if (!this.consentChecked) {
        this.validationError = true;
        return;
      }

      this.isSubmitting = true;
      this.submitError = false;
      this.validationError = false;

      try {
        await subscribeMailingList();
        this.submitSuccess = true;
        await sendButtonCtaTelemetry(
          this.$route.fullPath,
          "Newsletter Subscribe",
          false
        );
      } catch {
        this.submitError = true;
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
</script>

<template>
  <div
    v-if="showBanner"
    data-cy="newsletter-banner"
    class="relative my-16 py-10"
  >
    <!-- Left decorative shapes — pinned to left edge of viewport -->
    <img
      :src="leftShapeBadges"
      alt=""
      aria-hidden="true"
      class="pointer-events-none absolute left-10 hidden -translate-y-1/2 md:block"
      style="width: 136px; height: 156px; top: 50%"
    />

    <!-- Content -->
    <div
      class="relative z-10 mx-4 flex flex-col items-start justify-between gap-8 px-2 md:mx-6 md:flex-row md:items-center md:px-4 xxl:mx-20 xlg:mx-auto xlg:max-w-[100rem]"
    >
      <!-- Left content: SVG spacer + text -->
      <div class="flex shrink-0 items-center gap-4 md:gap-10 md:w-[48%]">
        <!-- Spacer to clear left SVG -->
        <div class="hidden shrink-0 md:block" style="width: 100px"></div>
        <div class="pr-6 md:pr-10">
          <h2
            class="text-2xl font-bold leading-tight text-black-dark md:text-[28px]"
          >
            Be the first to know about Cisco learning
          </h2>
          <p class="mt-3 text-base leading-relaxed text-black-light">
            Stay up to date with our latest news, exclusive offers, and
            more–straight to your inbox.
          </p>
        </div>
      </div>

      <!-- Right content: checkbox + button -->
      <div class="flex items-start gap-4 md:gap-6 md:w-[45%]">
        <div class="flex grow flex-col items-start gap-5">
          <label
            for="newsletter-consent"
            class="flex cursor-pointer items-start gap-3"
          >
            <input
              id="newsletter-consent"
              v-model="consentChecked"
              type="checkbox"
              :disabled="submitSuccess"
              data-cy="newsletter-consent-checkbox"
              class="mt-0.5 h-5 w-5 shrink-0 cursor-pointer appearance-none rounded-sm border-2 border-black-light checked:border-blue-light checked:bg-blue-light disabled:cursor-default"
            />
            <span class="text-sm leading-snug text-black-dark">
              I would like to receive emails about Learn with Cisco programs. I
              understand I can unsubscribe at any time.
            </span>
          </label>

          <output
            v-if="submitSuccess"
            aria-label="Subscription confirmation"
            class="flex items-center gap-2 rounded bg-green-50 px-3 py-2 text-sm font-medium text-green-800"
            data-cy="newsletter-success-msg"
          >
            <svg
              class="h-4 w-4 shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            Thank you. You're signed up to receive email updates and can
            unsubscribe at any time.
          </output>

          <p
            v-if="validationError && !submitSuccess"
            class="text-sm text-red-600"
            role="alert"
            data-cy="newsletter-error-msg"
          >
            To receive email updates, please check the box before selecting
            Submit.
          </p>

          <p
            v-if="submitError && !submitSuccess"
            class="text-sm text-red-600"
            role="alert"
            data-cy="newsletter-error-msg"
          >
            Something went wrong. Please try again.
          </p>

          <button
            v-if="!submitSuccess"
            data-cy="newsletter-submit-btn"
            :disabled="isSubmitting"
            class="rounded-full bg-blue-light px-8 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
            @click="handleSubmit"
            @keyup.enter="handleSubmit"
          >
            {{ isSubmitting ? "Submitting..." : "Submit" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
